import { selectTicketSalePageData } from 'app/pages/TicketSalePage/slice/selectors';
import { ITaxRate } from 'app/pages/TicketSalePage/slice/types';
import { TFunction } from 'i18next';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TicketPrice } from 'types/TicketsState';
import {
  applyDiscountToPrice,
  buildPriceToShow,
  getTaxPrice,
} from 'utils/tickets';
import { getPricePaidForASingleTicket } from './utils';
import {
  addCalendarTicketsToCart,
  addTicketsToCart,
} from 'app/pages/TicketSalePage/slice';
import { useDispatch } from 'react-redux';
import { EVENT_TYPE_WITH_CALENDAR } from 'utils/static/constants';
import { DateTime } from 'luxon';
import { dateFormatForCalendar } from 'utils/calendar';

interface TicketBulkOptionsProps {
  ticketId: string;
  ticketPrice: TicketPrice[];
  quantity: number;
  ticketAvailableQuantity: number;
  selectedDate: DateTime | undefined;
}

const TicketBulkOptions: React.FC<TicketBulkOptionsProps> = ({
  ticketId,
  ticketPrice,
  quantity,
  ticketAvailableQuantity,
  selectedDate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    selectedEvent: { type, currency, tax_included, tax_rate },
    voucher: {
      voucherData: { discount, discount_percent },
      voucherApplicableTickets,
    },
  } = useSelector(selectTicketSalePageData);

  const ticketIsDiscountable =
    voucherApplicableTickets.findIndex(el => el === ticketId) >= 0;

  const thePricePaidForASingleTicket = getPricePaidForASingleTicket(
    ticketPrice,
    tax_rate,
    tax_included,
  );

  const { bulkOptionSavedAmount, bulkOptionText, bulkOptionQuantity } =
    getBulkTicketText(
      ticketPrice,
      quantity,
      currency,
      tax_included,
      tax_rate,
      thePricePaidForASingleTicket,
      discount,
      discount_percent,
      ticketIsDiscountable,
      t,
    );

  const bulkOptionSaveAmountElement = document.getElementById(
    `bulk-option-save-amount-${ticketId}`,
  );

  const handleAddBulkOptionToCart = () => {
    if (type === EVENT_TYPE_WITH_CALENDAR && selectedDate) {
      dispatch(
        addCalendarTicketsToCart({
          ticketId,
          quantity: bulkOptionQuantity as number,
          date: selectedDate.toFormat(dateFormatForCalendar),
        }),
      );
    } else {
      dispatch(
        addTicketsToCart({
          ticketId,
          quantity: bulkOptionQuantity as number,
        }),
      );
    }
  };

  return (
    <>
      {bulkOptionText && bulkOptionQuantity <= ticketAvailableQuantity ? (
        <div className="sm:px-[12px] mb-[12px] flex gap-[4px] flex-wrap p2-medium text-green-1000">
          <p>{bulkOptionText}.</p>
          <button className="underline" onClick={handleAddBulkOptionToCart}>
            {t('ticketSale.buyNow')}
          </button>
        </div>
      ) : null}
      {bulkOptionSavedAmount > 0 && bulkOptionSaveAmountElement
        ? createPortal(
            <p className="green-label">
              {t('ticketSale.youSave')}
              {buildPriceToShow(bulkOptionSavedAmount, currency)}
            </p>,
            bulkOptionSaveAmountElement,
          )
        : null}
    </>
  );
};

const getBulkTicketText = (
  ticketPrices: TicketPrice[],
  quantity: number,
  eventCurrency: string,
  tax_included: boolean,
  tax_rate: ITaxRate,
  thePricePaidForASingleTicket,
  discount: number,
  discount_percent: boolean,
  ticketIsDiscountable: boolean,
  t: TFunction,
) => {
  const ticketBulkOptions = ticketPrices.slice(1, ticketPrices.length);
  const nextOptionIndex = ticketBulkOptions.findIndex(
    option => option.tickets > quantity,
  );
  const lastOptionIndex = ticketBulkOptions.length - 1;
  const bulkOptionIndex =
    nextOptionIndex >= 0 ? nextOptionIndex : lastOptionIndex;

  const bulkOption = {
    ...ticketBulkOptions[bulkOptionIndex],
  };

  let bulkOptionPrice = tax_included
    ? bulkOption.price + getTaxPrice(bulkOption.price, tax_rate.rate)
    : bulkOption.price;

  let oneTicketPrice = thePricePaidForASingleTicket;

  if (discount && ticketIsDiscountable) {
    oneTicketPrice = applyDiscountToPrice(
      oneTicketPrice,
      discount,
      discount_percent,
    );
    bulkOptionPrice = applyDiscountToPrice(
      bulkOptionPrice,
      discount,
      discount_percent,
    );

    oneTicketPrice = oneTicketPrice > 0 ? oneTicketPrice : 0;
    bulkOptionPrice = bulkOptionPrice > 0 ? bulkOptionPrice : 0;
  }

  const savedAmount = (oneTicketPrice - bulkOptionPrice) * bulkOption.tickets;

  if (bulkOptionPrice === 0)
    return {
      bulkOptionSavedAmount: 0,
      bulkOptionText: '',
      bulkOptionQuantity: 0,
    };

  const formattedSavedAmount = buildPriceToShow(savedAmount, eventCurrency);

  if (nextOptionIndex === 0) {
    return {
      bulkOptionSavedAmount: 0,
      bulkOptionText: t('ticketSale.firstBulkTicket', {
        tickets: ticketBulkOptions[nextOptionIndex].tickets,
        savedAmount: formattedSavedAmount,
      }),
      bulkOptionQuantity: ticketBulkOptions[nextOptionIndex].tickets,
    };
  } else if (nextOptionIndex > 0) {
    const currentOptionIndex = bulkOptionIndex - 1;

    const currentBulkOption = {
      ...ticketBulkOptions[currentOptionIndex],
    };

    let currentBulkOptionPrice = tax_included
      ? currentBulkOption.price +
        getTaxPrice(currentBulkOption.price, tax_rate.rate)
      : currentBulkOption.price;

    if (discount && ticketIsDiscountable) {
      currentBulkOptionPrice = applyDiscountToPrice(
        currentBulkOptionPrice,
        discount,
        discount_percent,
      );
      currentBulkOptionPrice =
        currentBulkOptionPrice > 0 ? currentBulkOptionPrice : 0;
    }

    let currentSavedAmount =
      (oneTicketPrice - currentBulkOptionPrice) * currentBulkOption.tickets;

    return {
      bulkOptionSavedAmount: currentSavedAmount,
      bulkOptionText: t('ticketSale.otherBulkTicket', {
        tickets: ticketBulkOptions[nextOptionIndex].tickets - quantity,
        nextSavedAmount: formattedSavedAmount,
      }),
      bulkOptionQuantity: ticketBulkOptions[nextOptionIndex].tickets,
    };
  } else {
    return {
      bulkOptionSavedAmount: savedAmount,
      bulkOptionText: '',
      bulkOptionQuantity: 0,
    };
  }
};

export default TicketBulkOptions;
